import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

import Banner2 from "../../assets/img/website-images/Blogdetailsbanner2.png";

/* ─── Variants ─────────────────────────────────────────────────── */
const heroVariants = {
  hidden: { opacity: 0, scale: 1.04 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
  },
};

/* ─── Paragraph with individual scroll trigger ──────────────────── */
function AnimPara({ children }) {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  return (
    <motion.p
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, ease: "easeOut" }}
    >
      {children}
    </motion.p>
  );
}

/* ─── Main Component ───────────────────────────────────────────── */
function BlogTwo() {
  const { ref: heroRef, inView: heroInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const { ref: titleRef, inView: titleInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  /* GSAP: hero image clip-path wipe */
  const imgWrapRef = useRef(null);
  useEffect(() => {
    if (heroInView && imgWrapRef.current) {
      gsap.fromTo(
        imgWrapRef.current,
        { clipPath: "inset(0 100% 0 0)", opacity: 1 },
        {
          clipPath: "inset(0 0% 0 0)",
          duration: 1.1,
          ease: "power3.inOut",
          delay: 0.1,
        },
      );
    }
  }, [heroInView]);

  /* GSAP: hero image scroll parallax */
  const heroImgRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!heroImgRef.current) return;
      const rect = heroImgRef.current.getBoundingClientRect();
      const progress = 1 - rect.bottom / (window.innerHeight + rect.height);
      gsap.set(heroImgRef.current, { y: progress * 30 });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* GSAP: reading progress bar */
  const progressRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!progressRef.current) return;
      const scrollTop = window.scrollY;
      const docHeight = document.body.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      gsap.set(progressRef.current, { width: `${progress}%` });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Reading progress bar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "3px",
          background: "transparent",
          zIndex: 9999,
          width: "100%",
          pointerEvents: "none",
        }}
      >
        <div
          ref={progressRef}
          style={{
            height: "100%",
            width: "0%",
            background: "linear-gradient(90deg, #1E5AA8, #F27024)",
            borderRadius: "0 2px 2px 0",
          }}
        />
      </div>

      <section className="project-detail">
        <div className="container">
          {/* ── Hero Image ── */}
          <div className="upper-box" ref={heroRef}>
            <motion.div
              variants={heroVariants}
              initial="hidden"
              animate={heroInView ? "visible" : "hidden"}
              style={{ borderRadius: "12px", overflow: "hidden" }}
            >
              <div
                ref={imgWrapRef}
                style={{ overflow: "hidden", borderRadius: "12px" }}
              >
                <figure
                  className="image"
                  style={{ margin: 0, overflow: "hidden" }}
                >
                  <img
                    ref={heroImgRef}
                    src={Banner2}
                    alt="Blog Banner"
                    style={{
                      display: "block",
                      width: "100%",
                      transformOrigin: "center",
                    }}
                  />
                </figure>
              </div>
            </motion.div>
          </div>

          {/* ── Article Content ── */}
          <div className="lower-content2">
            <div className="row">
              <div className="text-column col-lg-12 col-md-12 col-sm-12">
                <div className="s-about-content">
                  {/* Title */}
                  <motion.h2
                    ref={titleRef}
                    variants={titleVariants}
                    initial="hidden"
                    animate={titleInView ? "visible" : "hidden"}
                  >
                    Essential Skills Every Pharmacy Student Should Develop
                  </motion.h2>

                  <AnimPara delay={0}>
                    Pharmacy is a profession that combines scientific knowledge
                    with patient care and responsibility. While academic
                    learning forms the foundation of pharmacy education, success
                    in this field depends heavily on the development of
                    practical and professional skills. Pharmacy students who
                    focus on building the right skills during their studies are
                    better prepared to handle real-world challenges and
                    contribute effectively to the healthcare system.
                  </AnimPara>

                  <AnimPara delay={0}>
                    One of the most important skills every pharmacy student
                    should develop is strong communication. Pharmacists
                    regularly interact with patients, doctors, nurses, and other
                    healthcare professionals. Clear communication helps ensure
                    that patients understand how to use medications safely and
                    correctly. It also helps healthcare teams collaborate
                    effectively, reducing the risk of errors and improving
                    patient outcomes.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Attention to detail is another critical skill in the
                    pharmaceutical field. Even small mistakes in medication
                    dosage, labeling, or instructions can have serious
                    consequences for patient health. Pharmacy students must
                    learn to carefully read prescriptions, verify information,
                    and follow procedures accurately. Developing a habit of
                    precision and careful observation is essential for
                    maintaining safety and professionalism in pharmacy practice.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Problem-solving and critical thinking skills are equally
                    important for pharmacy students. Healthcare situations often
                    require quick decisions based on available information.
                    Pharmacists must analyze symptoms, review patient histories,
                    and identify potential drug interactions. By practicing
                    analytical thinking during laboratory sessions and case
                    studies, students can strengthen their ability to make
                    informed decisions in complex situations.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Time management and organizational skills also play a
                    significant role in academic and professional success.
                    Pharmacy students balance lectures, laboratory work,
                    assignments, and examinations, while pharmacists in the
                    workplace manage prescriptions, patient consultations, and
                    inventory. Learning how to prioritize tasks and manage time
                    efficiently helps students maintain productivity and reduce
                    stress.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Finally, developing empathy and a strong sense of
                    responsibility is essential for anyone entering the
                    healthcare profession. Pharmacists are trusted professionals
                    who directly impact patient health and safety. By
                    cultivating compassion, ethical behavior, and a commitment
                    to continuous learning, pharmacy students can build
                    meaningful careers and contribute positively to society.
                  </AnimPara>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default BlogTwo;
